export const CHUNK_UPDATE_NEEDED_EVENT = 'chunk-update-needed';

const APP_SHELL_WORKER_PATH = '/service-worker.js';
const UPDATE_ATTEMPT_KEY = 'care_app_update_attempt';
const UPDATE_ATTEMPT_WINDOW_MS = 60_000;
const ACTIVATION_TIMEOUT_MS = 4_000;

function defaultEnvironment() {
  return {
    location: window.location,
    serviceWorker: navigator.serviceWorker,
    sessionStorage: window.sessionStorage,
    window,
  };
}

function workerScriptUrl(registration) {
  return (
    registration?.waiting?.scriptURL ||
    registration?.installing?.scriptURL ||
    registration?.active?.scriptURL ||
    ''
  );
}

export function isAppShellRegistration(registration) {
  if (!registration) return false;

  try {
    return new URL(workerScriptUrl(registration)).pathname.endsWith(APP_SHELL_WORKER_PATH);
  } catch {
    return false;
  }
}

export async function findAppShellRegistration(preferred, serviceWorker) {
  if (isAppShellRegistration(preferred)) return preferred;
  if (!serviceWorker?.getRegistrations) return null;

  const registrations = await serviceWorker.getRegistrations();
  return registrations.find(isAppShellRegistration) || null;
}

export function wasUpdateRecentlyAttempted(storage = window.sessionStorage, now = Date.now()) {
  const attemptedAt = Number(storage?.getItem(UPDATE_ATTEMPT_KEY) || '0');
  return now - attemptedAt < UPDATE_ATTEMPT_WINDOW_MS;
}

function markUpdateAttempt(storage, now = Date.now()) {
  storage?.setItem(UPDATE_ATTEMPT_KEY, String(now));
}

function reloadOnce(environment) {
  if (environment.window.__careAppReloading) return;
  environment.window.__careAppReloading = true;
  environment.location.reload();
}

function cacheBustedUrl(location, now = Date.now()) {
  const url = new URL(location.href);
  url.searchParams.set('__care_update', String(now));
  return url.toString();
}

async function recoverWithoutAppShell(registration, environment) {
  const appShellRegistration = await findAppShellRegistration(
    registration,
    environment.serviceWorker,
  );
  if (appShellRegistration) {
    await appShellRegistration.unregister();
  }

  environment.window.__careAppReloading = true;
  environment.location.replace(cacheBustedUrl(environment.location));
}

function waitForInstallation(registration, timeoutMs, environment) {
  const installing = registration?.installing;
  if (!installing) return Promise.resolve(registration?.waiting || null);

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      environment.window.clearTimeout(timeout);
      installing.removeEventListener?.('statechange', onStateChange);
      resolve(registration.waiting || null);
    };
    const onStateChange = () => {
      if (installing.state === 'installed' || installing.state === 'redundant') {
        finish();
      }
    };
    const timeout = environment.window.setTimeout(finish, timeoutMs);
    installing.addEventListener?.('statechange', onStateChange);
    onStateChange();
  });
}

/**
 * Activates the Care app-shell worker, then lets the global controllerchange
 * listener reload every tab that was already running under the old worker.
 */
export async function activateAppUpdate(
  registration,
  { forceRecovery = false, environment: environmentOverride } = {},
) {
  const environment = environmentOverride || defaultEnvironment();
  markUpdateAttempt(environment.sessionStorage);
  // Recovery is underway; a stale notice must not re-show the banner.
  environment.window.__chunkUpdateNotice = null;

  const appShellRegistration = await findAppShellRegistration(
    registration,
    environment.serviceWorker,
  );

  if (forceRecovery) {
    await recoverWithoutAppShell(appShellRegistration, environment);
    return;
  }

  if (!appShellRegistration) {
    reloadOnce(environment);
    return;
  }

  let waiting = appShellRegistration.waiting;
  if (!waiting) {
    try {
      await appShellRegistration.update();
      waiting = await waitForInstallation(
        appShellRegistration,
        ACTIVATION_TIMEOUT_MS,
        environment,
      );
    } catch {
      // A normal reload remains safe; a repeated failure uses targeted recovery.
    }
  }

  if (!waiting) {
    reloadOnce(environment);
    return;
  }

  waiting.postMessage({ type: 'SKIP_WAITING' });
  environment.window.setTimeout(() => reloadOnce(environment), ACTIVATION_TIMEOUT_MS);
}

/**
 * Existing tabs must reload together when clientsClaim() promotes a new
 * worker, or their running bundle goes stale against the purged precache.
 *
 * This also covers uncontrolled tabs that already have an active app-shell
 * worker (e.g. after a hard reload): when a new deploy claims such a tab, it
 * reloads into a consistent state. Only true first visits (no active worker
 * yet) skip the reload, so the initial clientsClaim() does not reload them.
 */
export function installControllerChangeReload(
  environmentOverride,
) {
  const environment = environmentOverride || defaultEnvironment();
  const serviceWorker = environment.serviceWorker;
  if (!serviceWorker || environment.window.__careControllerChangeReloadInstalled) {
    return Promise.resolve();
  }

  const install = () => {
    if (environment.window.__careControllerChangeReloadInstalled) return;
    environment.window.__careControllerChangeReloadInstalled = true;
    serviceWorker.addEventListener('controllerchange', () => reloadOnce(environment));
  };

  if (serviceWorker.controller) {
    install();
    return Promise.resolve();
  }

  return findAppShellRegistration(null, serviceWorker)
    .then((registration) => {
      if (registration?.active) install();
    })
    .catch(() => {});
}
